import type { User } from '@lmc/ts-common'
import { Add, Button, Form, FormCard, PageHeader } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Switch } from 'antd'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { PageWrapper } from '../components'
import { handleOnFinishFailed } from '../formComponents/utils'
import { useGetFunders } from '../hooks/useFunders'
import {
  type InvoicingRateCardDto,
  useGetInvoicingRateCards,
  useGetInvoicingSettings,
  useUpdateInvoicingRateCards,
  useUpdateInvoicingSettings
} from '../hooks/useInvoicingRates'
import type { InvoicingRateCard } from '../types/invoicingRates'
import FixedFooter from './components/FixedFooter'
import InvoicingRateCardComponent, {
  type InvoicingRateCardForm
} from './components/InvoicingRateCard'

type InvoicingRatesSettingsForm = {
  invoicingRates: InvoicingRateCardForm[]
}

const parseCards = (cards: InvoicingRateCard[]): InvoicingRateCardForm[] =>
  cards.map(({ _id, initialId, name, baseRate, effectiveDate, overrides }) => {
    const effectiveDateTime = effectiveDate ? dayjs(effectiveDate) : null
    return {
      _id,
      initialId,
      name,
      baseRate: parseFloat(baseRate) || 0,
      effectiveDate: effectiveDateTime,
      effectiveTime: effectiveDateTime,
      overrides: (overrides || []).map((o) => ({
        ...o,
        rate: parseFloat(o.rate) || 0
      }))
    }
  })

const buildEffectiveFrom = (
  card: InvoicingRateCardForm
): string | undefined => {
  if (!card.effectiveDate || !card.effectiveTime) {
    return undefined
  }
  return dayjs(card.effectiveDate)
    .set('hour', dayjs(card.effectiveTime).hour())
    .set('minute', dayjs(card.effectiveTime).minute())
    .set('second', 0)
    .set('millisecond', 0)
    .toISOString()
}

const cardToDto = (card: InvoicingRateCardForm): InvoicingRateCardDto => ({
  _id: card._id,
  name: card.name,
  baseRate: String(card.baseRate || 0),
  effectiveDate: buildEffectiveFrom(card),
  overrides: (card.overrides || [])
    .filter((o) => !o.isDeleted)
    .map((o) => ({
      level: o.level,
      eventTypeId: o.eventTypeId,
      rate: String(o.rate || 0)
    }))
})

const formToApiPayload = (
  formCards: InvoicingRateCardForm[],
  originalCards: InvoicingRateCardForm[]
): InvoicingRateCardDto[] => {
  const result: InvoicingRateCardDto[] = []
  const originalDtoById = new Map(
    originalCards
      .filter((c) => c._id)
      .map((c) => [c._id, JSON.stringify(cardToDto(c))])
  )

  for (const card of formCards) {
    // Skip new cards that were deleted before saving
    if (card.isDeleted && !card._id) {
      continue
    }

    if (card.isDeleted) {
      result.push({ _id: card._id, deleted: true })
      continue
    }

    // New card — always include
    if (!card._id) {
      result.push(cardToDto(card))
      continue
    }

    // Existing card — only include if changed
    const originalJson = originalDtoById.get(card._id)
    if (!originalJson || JSON.stringify(cardToDto(card)) !== originalJson) {
      result.push(cardToDto(card))
    }
  }

  return result
}

const InvoicingRatesSettings = () => {
  const [form] = AntdForm.useForm()
  const [isCardsDirty, setIsCardsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [unsavedToggle, setUnsavedToggle] = useState<boolean | null>(null)
  const isDirty = unsavedToggle !== null || isCardsDirty

  const sendEvent = useMixpanelEvent()
  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const { data: invoicingSettings, isLoading: isSettingsLoading } =
    useGetInvoicingSettings()
  const { updateInvoicingSettings } = useUpdateInvoicingSettings()

  const invoicingEnabled =
    unsavedToggle !== null
      ? unsavedToggle
      : (invoicingSettings?.enabled ?? false)

  const {
    data: invoicingRateCards,
    isLoading: isCardsLoading,
    refetch: refetchCards
  } = useGetInvoicingRateCards(!isSettingsLoading && invoicingEnabled)
  const { updateInvoicingRateCards } = useUpdateInvoicingRateCards()

  const { data: funders } = useGetFunders()

  const initialValues = useMemo<InvoicingRatesSettingsForm>(
    () => ({
      invoicingRates: invoicingRateCards?.length
        ? parseCards(invoicingRateCards)
        : []
    }),
    [invoicingRateCards]
  )

  const lockedById = useMemo(() => {
    const map = new Map<string, boolean>()

    invoicingRateCards?.forEach((c) => {
      map.set(c._id, c.locked)
    })
    return map
  }, [invoicingRateCards])

  const getAffectedContractCount = (initialId: string | undefined): number => {
    if (!initialId) {
      return 0
    }
    return (funders ?? [])
      .flatMap((f) => f.contracts)
      .filter((c) => !c.deleted && c.invoicingRateCardInitialId === initialId)
      .length
  }

  const handleSave = async () => {
    try {
      setIsUpdateLoading(true)

      if (invoicingEnabled && isCardsDirty) {
        await form.validateFields()
      }

      if (unsavedToggle !== null) {
        await updateInvoicingSettings({ enabled: invoicingEnabled })
        sendEvent(MixpanelEvents.INVOICING_TOGGLED, {
          teamMemberName:
            `${user?.name?.first ?? ''} ${user?.name?.last ?? ''}`.trim(),
          invoicingEnabled
        })
      }

      if (invoicingEnabled && isCardsDirty) {
        const values = form.getFieldsValue(true) as InvoicingRatesSettingsForm
        const payload = formToApiPayload(
          values.invoicingRates,
          initialValues.invoicingRates
        )
        if (payload.length > 0) {
          await updateInvoicingRateCards({ cards: payload })
          const { data: freshCards } = await refetchCards()
          if (freshCards) {
            form.setFieldsValue({ invoicingRates: parseCards(freshCards) })
          }
        }
      }

      showToast({ title: 'Invoicing rates updated' })
      setUnsavedToggle(null)
      setIsCardsDirty(false)
    } catch (err: any) {
      if (err?.errorFields) {
        handleOnFinishFailed(err.errorFields)
        return
      }
      showToast({
        title: err?.message || 'Failed to save settings',
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const newRate: InvoicingRateCardForm = {
    name: '',
    baseRate: undefined as unknown as number,
    effectiveDate: null,
    effectiveTime: null,
    overrides: []
  }

  return (
    <>
      <PageHeader
        title="Invoicing Rates"
        subtitle="Set up invoicing rate cards for your organisation."
        sticky
      />

      <PageWrapper>
        {isSettingsLoading ? (
          <Spinner />
        ) : (
          <>
            <FormCard
              title={
                <EnableToggleRow>
                  <Text>Enable invoicing rates</Text>
                  <Switch
                    checked={invoicingEnabled}
                    onChange={setUnsavedToggle}
                  />
                </EnableToggleRow>
              }
            />

            {invoicingEnabled && !isCardsLoading && (
              <Form<InvoicingRatesSettingsForm>
                form={form}
                onValuesChange={() => setIsCardsDirty(true)}
                initialValues={initialValues}
                validateTrigger={['onBlur', 'onChange']}
              >
                <AntdForm.List name="invoicingRates">
                  {(fields, { add }) => (
                    <FormCard
                      title="Invoicing rate cards"
                      rightChildren={
                        <Button
                          type="button"
                          variant="text"
                          icon={<Add />}
                          onClick={() => {
                            setIsCardsDirty(true)
                            add(newRate)
                          }}
                        >
                          Add rate
                        </Button>
                      }
                    >
                      {fields.filter((f) => {
                        const card = form.getFieldValue([
                          'invoicingRates',
                          f.name
                        ])
                        return !card?.isDeleted
                      }).length === 0 && (
                        <EmptyText>
                          No invoicing rate cards set up yet.
                        </EmptyText>
                      )}
                      {fields.map((field) => {
                        const card = form.getFieldValue([
                          'invoicingRates',
                          field.name
                        ]) as InvoicingRateCardForm | undefined
                        if (card?.isDeleted) {
                          return null
                        }

                        const isLocked = !!(
                          card?._id && lockedById.get(card._id)
                        )
                        const affectedCount = getAffectedContractCount(
                          card?.initialId
                        )
                        const deleteWarningText =
                          !isLocked && affectedCount > 0
                            ? `${affectedCount} funder contract${affectedCount !== 1 ? 's' : ''} use this rate card. Deleting it will remove their rate card assignment.`
                            : undefined

                        // Effective date is only required when this is a new version
                        // of an existing card (scheduled rate change), not a brand new rate card
                        const requireEffectiveDate = !!card?.isNewVersion

                        const handleScheduleFutureRate =
                          card?._id && !isLocked
                            ? () => {
                                setIsCardsDirty(true)
                                add({
                                  name: card.name,
                                  baseRate: card.baseRate,
                                  effectiveDate: null,
                                  effectiveTime: null,
                                  overrides: (card.overrides ?? []).filter(
                                    (o) => !o.isDeleted
                                  ),
                                  isNewVersion: true
                                })
                              }
                            : undefined

                        return (
                          <InvoicingRateCardComponent
                            key={field.key}
                            fieldName={field.name}
                            form={form}
                            onDirty={() => setIsCardsDirty(true)}
                            isLocked={isLocked}
                            requireEffectiveDate={requireEffectiveDate}
                            onScheduleFutureRate={handleScheduleFutureRate}
                            deleteWarningText={deleteWarningText}
                            originalCard={
                              initialValues.invoicingRates[field.name]
                            }
                          />
                        )
                      })}
                    </FormCard>
                  )}
                </AntdForm.List>
              </Form>
            )}
          </>
        )}
      </PageWrapper>

      <FixedFooter
        isUpdateLoading={isUpdateLoading}
        isDirty={isDirty}
        handleSubmit={handleSave}
      />

      <NavigationModal
        canNavigate={!isDirty}
        modalContentText="You have unsaved changes. Are you sure you want to leave?"
      />
    </>
  )
}

const EnableToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EmptyText = styled(Text)`
  text-align: center;
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default InvoicingRatesSettings
